import React, { useState } from 'react';
import {
    Box,
    TextField,
    Button,
    Typography,
    Alert,
    Link,
    Divider,
    InputAdornment,
    IconButton,
    Paper
} from '@mui/material';
import { Visibility, VisibilityOff, Email, Lock } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { getCleanErrorMessage } from '../../utils/errorUtils';

interface LoginFormProps {
    onSuccess: () => void;
    onSwitchToRegister: () => void;
    onSwitchToReset: () => void;
}

const LoginForm: React.FC<LoginFormProps> = ({ onSuccess, onSwitchToRegister, onSwitchToReset }) => {
    const { t } = useTranslation();
    const { login, isLoading, error } = useAuth();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [localError, setLocalError] = useState('');

    const handleSubmit = async (e: React.FormEvent) => {
        console.log('🔐 LoginForm: Form submit started');
        e.preventDefault();
        setLoading(true);
        setLocalError('');

        console.log('🔐 LoginForm: Email:', email);
        console.log('🔐 LoginForm: Password length:', password.length);
        console.log('🔐 LoginForm: Calling AuthContext login...');

        try {
            await login(email, password);
            console.log('🔐 LoginForm: Login successful, calling onSuccess');
            onSuccess();
        } catch (err: any) {
            console.error('🔐 LoginForm: Login failed:', err);
            setLocalError(getCleanErrorMessage(err) || t('auth.loginError'));
        } finally {
            console.log('🔐 LoginForm: Login attempt completed');
            setLoading(false);
        }
    };

    return (
        <Paper elevation={3} sx={{ p: 4, maxWidth: 400, mx: 'auto' }}>
            <Box sx={{ textAlign: 'center', mb: 3 }}>
                <Typography variant="h4" component="h1" gutterBottom>
                    {t('auth.login')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    {t('auth.loginSubtitle')}
                </Typography>
            </Box>

            {(error || localError) && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error || localError}
                </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                    fullWidth
                    label={t('auth.email')}
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <Email />
                            </InputAdornment>
                        ),
                    }}
                />

                <TextField
                    fullWidth
                    label={t('auth.password')}
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <Lock />
                            </InputAdornment>
                        ),
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton
                                    onClick={() => setShowPassword(!showPassword)}
                                    edge="end"
                                >
                                    {showPassword ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                />

                <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    size="large"
                    disabled={loading || isLoading}
                    sx={{ mt: 2 }}
                >
                    {loading || isLoading ? t('common.loading') : t('auth.login')}
                </Button>

                <Box sx={{ textAlign: 'center', mt: 2 }}>
                    <Link
                        component="button"
                        type="button"
                        onClick={onSwitchToReset}
                        sx={{ fontSize: '0.875rem' }}
                    >
                        {t('auth.forgotPassword')}
                    </Link>
                </Box>

                <Divider sx={{ my: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                        {t('auth.or')}
                    </Typography>
                </Divider>

                <Button
                    fullWidth
                    variant="outlined"
                    onClick={onSwitchToRegister}
                    sx={{ mt: 1 }}
                >
                    {t('auth.createAccount')}
                </Button>
            </Box>
        </Paper>
    );
};

export default LoginForm;
